import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { logger } from '../utils/logger';
import liveStreamService, { LiveStream } from '../services/liveStreams';
import './LiveStreams.css';

const LiveStreams: React.FC = () => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState<LiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'live' | 'upcoming'>('all');

  useEffect(() => {
    loadStreams();
    // Auto-refresh every 30 seconds to update viewer counts and status
    const interval = setInterval(loadStreams, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStreams = async () => {
    try {
      setLoading(true);
      const data = await liveStreamService.getAllLiveStreams();
      setStreams(data);
      setError(null);
    } catch (err: any) {
      logger.error('Error loading live streams:', err);
      setError(err.message || 'Failed to load live streams');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStreams = () => {
    switch (filter) {
      case 'live':
        return streams.filter(s => s.status === 'live');
      case 'upcoming':
        return streams.filter(s =>
          s.scheduledStartTime && s.scheduledStartTime > Date.now() &&
          (s.status === 'ready' || s.status === 'created')
        );
      default:
        return streams;
    }
  };

  const getStatusBadge = (stream: LiveStream) => {
    switch (stream.status) {
      case 'live':
        return (
          <span className="status-badge live">
            <span className="pulse-dot"></span>
            LIVE
          </span>
        );
      case 'starting':
        return <span className="status-badge starting">Starting</span>;
      case 'ready':
        if (stream.scheduledStartTime && stream.scheduledStartTime > Date.now()) {
          return <span className="status-badge upcoming">Upcoming</span>;
        }
        return <span className="status-badge ready">Ready</span>;
      case 'provisioning':
        return <span className="status-badge provisioning">Setting Up</span>;
      case 'stopped':
        return <span className="status-badge stopped">Ended</span>;
      case 'error':
        return <span className="status-badge error">Error</span>;
      default:
        return <span className="status-badge">{stream.status}</span>;
    }
  };

  const formatViewerCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const formatScheduledTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredStreams = getFilteredStreams();

  if (loading && streams.length === 0) {
    return (
      <div className="live-streams-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-circle"></div>
              <div className="spinner-circle"></div>
              <div className="spinner-circle"></div>
            </div>
            <p>Loading live streams...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="live-streams-page">
      {/* Animated Background */}
      <div className="streams-background">
        <div className="wave-line wave-1"></div>
        <div className="wave-line wave-2"></div>
        <div className="wave-line wave-3"></div>
      </div>

      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="title-icon">
                <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2"/>
                <circle cx="16" cy="16" r="4" fill="currentColor"/>
                <path d="M16 4V8M16 24V28M28 16H24M8 16H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Live Streams
            </h1>
            <p className="page-description">
              Watch live broadcasts from Tempest Radio creators
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Streams
              <span className="tab-count">{streams.length}</span>
            </button>
            <button
              className={`filter-tab ${filter === 'live' ? 'active' : ''}`}
              onClick={() => setFilter('live')}
            >
              <span className="pulse-dot small"></span>
              Live Now
              <span className="tab-count">
                {streams.filter(s => s.status === 'live').length}
              </span>
            </button>
            <button
              className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
              <span className="tab-count">
                {streams.filter(s =>
                  s.scheduledStartTime && s.scheduledStartTime > Date.now()
                ).length}
              </span>
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="error-banner">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {error}
            <button onClick={loadStreams} className="retry-button">Retry</button>
          </div>
        )}

        {/* Streams Grid */}
        {filteredStreams.length === 0 ? (
          <div className="empty-state">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="empty-icon">
              <circle cx="40" cy="40" r="30" stroke="currentColor" strokeWidth="2"/>
              <circle cx="40" cy="40" r="10" fill="currentColor" opacity="0.3"/>
            </svg>
            <h3>No {filter !== 'all' ? filter : ''} streams found</h3>
            <p>
              {filter === 'live' && 'There are no live streams at the moment. Check back soon!'}
              {filter === 'upcoming' && 'No streams are scheduled. Check back later for upcoming broadcasts.'}
              {filter === 'all' && 'No streams available yet. Be the first to go live!'}
            </p>
          </div>
        ) : (
          <div className="streams-grid">
            {filteredStreams.map((stream) => (
              <div
                key={stream.streamId}
                className={`stream-card ${stream.status === 'live' ? 'live' : ''}`}
                onClick={() => navigate(`/live/${stream.streamId}`)}
              >
                {/* Thumbnail */}
                <div className="stream-thumbnail">
                  {stream.thumbnailUrl ? (
                    <img src={stream.thumbnailUrl} alt={stream.title} />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                        <circle cx="30" cy="30" r="20" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="30" cy="30" r="6" fill="currentColor"/>
                      </svg>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="thumbnail-status">
                    {getStatusBadge(stream)}
                  </div>

                  {/* Viewer Count (if live) */}
                  {stream.status === 'live' && (
                    <div className="viewer-count">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 3C4.5 3 1.5 6 0 8c1.5 2 4.5 5 8 5s6.5-3 8-5c-1.5-2-4.5-5-8-5zm0 8c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z"/>
                        <circle cx="8" cy="8" r="2"/>
                      </svg>
                      {formatViewerCount(stream.viewerCount)}
                    </div>
                  )}

                  {/* Scheduled Time (if upcoming) */}
                  {stream.scheduledStartTime && stream.scheduledStartTime > Date.now() && (
                    <div className="scheduled-time">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1" fill="none"/>
                        <path d="M7 3V7L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      {formatScheduledTime(stream.scheduledStartTime)}
                    </div>
                  )}
                </div>

                {/* Stream Info */}
                <div className="stream-info">
                  <h3 className="stream-title">{stream.title}</h3>
                  <p className="stream-creator">{stream.userName || 'Tempest Radio'}</p>

                  {stream.description && (
                    <p className="stream-description">{stream.description}</p>
                  )}

                  {/* Tags */}
                  {stream.tags && stream.tags.length > 0 && (
                    <div className="stream-tags">
                      {stream.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                      {stream.tags.length > 3 && (
                        <span className="tag more">+{stream.tags.length - 3}</span>
                      )}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="stream-stats">
                    {stream.status === 'live' && stream.startedAt && (
                      <span className="stat">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M7 3V7L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {Math.floor((Date.now() - stream.startedAt) / 60000)}m ago
                      </span>
                    )}
                    {stream.category && (
                      <span className="stat category">{stream.category}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStreams;
